/**
 * Hook for managing document review polygons (annotations)
 *
 * Uses the real API via useDocumentSuggestions hooks instead of mock localStorage data.
 * Provides conversion between frontend ReviewPolygon format and API DocumentSuggestion format.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import {
    useDocumentSuggestions,
    useCreateSuggestion,
    useUpdateSuggestion,
    useDeleteSuggestion,
    type DocumentSuggestion,
    type PolygonCoordinates,
} from "@/hooks/api/use-document-suggestions";
import type { ReviewPolygon } from "@/types/document-review";
import { toast } from "sonner";

// ============================================================================
// Conversion Utilities
// ============================================================================

/**
 * Convert API DocumentSuggestion to frontend ReviewPolygon format
 */
function suggestionToPolygon(suggestion: DocumentSuggestion): ReviewPolygon {
    const coords = suggestion.polygon_attributes || { x: 0, y: 0, width: 0.1, height: 0.05 };
    const attributes = suggestion.attribute_metadata as Record<string, string> | null;

    return {
        id: suggestion.document_user_suggestion_id,
        name: suggestion.name || undefined,
        pageNumber: suggestion.document_page || 1,
        x: coords.x,
        y: coords.y,
        width: coords.width,
        height: coords.height,
        notes: suggestion.description || undefined,
        attributes: attributes || undefined,
        comments: [], // Comments are fetched separately via useSuggestionChat
        createdAt: suggestion.created_at,
        updatedAt: suggestion.updated_at,
        status: suggestion.status,
        priority: suggestion.priority,
        aiSuggestedValue: suggestion.ai_suggested_value || undefined,
        aiConfidence: suggestion.ai_suggestion_confidence || undefined,
        resolvedAt: suggestion.resolved_at || undefined,
        resolvedBy: suggestion.resolved_by || undefined,
    };
}

/**
 * Convert frontend polygon partial to API request format
 */
function polygonToSuggestionRequest(
    partial: Omit<ReviewPolygon, "id" | "createdAt" | "updatedAt">
): {
    name: string;
    description?: string;
    document_page?: number;
    polygon_coordinates?: PolygonCoordinates;
    priority?: string;
    attributes?: Record<string, unknown>;
} {
    return {
        name: partial.name || "New Annotation",
        description: partial.notes,
        document_page: partial.pageNumber,
        polygon_coordinates: {
            x: partial.x,
            y: partial.y,
            width: partial.width,
            height: partial.height,
            normalized: true,
        },
        priority: partial.priority || "medium",
        attributes: partial.attributes,
    };
}

// ============================================================================
// Hook
// ============================================================================

export function useDocumentPolygons(documentId: string | undefined) {
    const [selectedPolygon, setSelectedPolygon] = useState<ReviewPolygon | null>(null);

    // Fetch suggestions from API
    const {
        data: suggestions = [],
        isLoading,
        error,
    } = useDocumentSuggestions(documentId, !!documentId);

    // Mutations
    const createMutation = useCreateSuggestion();
    const updateMutation = useUpdateSuggestion();
    const deleteMutation = useDeleteSuggestion();

    // Convert suggestions to polygons - MEMOIZED to prevent infinite loops
    // Without useMemo, this creates a new array reference on every render,
    // which triggers the useEffect below, causing setSelectedPolygon,
    // which triggers another render, creating an infinite loop.
    const polygons: ReviewPolygon[] = useMemo(
        () => suggestions.map(suggestionToPolygon),
        [suggestions]
    );

    // Update selected polygon when polygons change (e.g., after a mutation)
    useEffect(() => {
        if (selectedPolygon) {
            const updated = polygons.find(p => p.id === selectedPolygon.id);
            // Only update if the polygon data actually changed
            if (updated && JSON.stringify(updated) !== JSON.stringify(selectedPolygon)) {
                setSelectedPolygon(updated);
            }
        }
    }, [polygons, selectedPolygon]);

    // Create a new polygon
    const createPolygon = useCallback(
        (partial: Omit<ReviewPolygon, "id" | "createdAt" | "updatedAt">) => {
            if (!documentId) {
                console.warn('[useDocumentPolygons] No documentId, cannot create polygon');
                return;
            }

            const request = polygonToSuggestionRequest(partial);
            console.log('[useDocumentPolygons] Creating polygon via API:', request);

            createMutation.mutate(
                { documentId, ...request },
                {
                    onSuccess: (result) => {
                        console.log('[useDocumentPolygons] Polygon created:', result.document_user_suggestion_id);
                        // Select the newly created polygon
                        setSelectedPolygon(suggestionToPolygon(result));
                    },
                    onError: (err) => {
                        console.error('[useDocumentPolygons] Failed to create polygon:', err);
                    },
                }
            );
        },
        [documentId, createMutation]
    );

    // Update an existing polygon
    const updatePolygon = useCallback(
        (polygonId: string, updates: Partial<ReviewPolygon>) => {
            if (!documentId) {
                console.warn('[useDocumentPolygons] No documentId, cannot update polygon');
                return;
            }

            console.log('[useDocumentPolygons] Updating polygon via API:', polygonId, updates);

            // Map frontend updates to API format
            const apiUpdates: Record<string, unknown> = {};

            if (updates.name !== undefined) apiUpdates.name = updates.name;
            if (updates.notes !== undefined) apiUpdates.description = updates.notes;
            if (updates.pageNumber !== undefined) apiUpdates.document_page = updates.pageNumber;
            if (updates.status !== undefined) apiUpdates.status = updates.status;
            if (updates.priority !== undefined) apiUpdates.priority = updates.priority;
            if (updates.attributes !== undefined) apiUpdates.attributes = updates.attributes;

            // If position/size changed, update polygon_coordinates
            if (
                updates.x !== undefined ||
                updates.y !== undefined ||
                updates.width !== undefined ||
                updates.height !== undefined
            ) {
                const existingPolygon = polygons.find(p => p.id === polygonId);
                if (existingPolygon) {
                    apiUpdates.polygon_coordinates = {
                        x: updates.x ?? existingPolygon.x,
                        y: updates.y ?? existingPolygon.y,
                        width: updates.width ?? existingPolygon.width,
                        height: updates.height ?? existingPolygon.height,
                        normalized: true,
                    };
                }
            }

            updateMutation.mutate(
                { suggestionId: polygonId, ...apiUpdates },
                {
                    onSuccess: (result) => {
                        console.log('[useDocumentPolygons] Polygon updated:', result.document_user_suggestion_id);
                        // Update selected if it was the one being updated
                        if (selectedPolygon?.id === polygonId) {
                            setSelectedPolygon(suggestionToPolygon(result));
                        }
                        toast.success('Modifiche salvate');
                    },
                    onError: (err) => {
                        console.error('[useDocumentPolygons] Failed to update polygon:', err);
                        toast.error('Errore nel salvataggio');
                    },
                }
            );
        },
        [documentId, polygons, selectedPolygon, updateMutation]
    );

    // Delete a polygon
    const deletePolygon = useCallback(
        (polygonId: string) => {
            if (!documentId) {
                console.warn('[useDocumentPolygons] No documentId, cannot delete polygon');
                return;
            }

            console.log('[useDocumentPolygons] Deleting polygon via API:', polygonId);

            deleteMutation.mutate(
                { suggestionId: polygonId, documentId },
                {
                    onSuccess: () => {
                        console.log('[useDocumentPolygons] Polygon deleted:', polygonId);
                        // Clear selection if the deleted polygon was selected
                        if (selectedPolygon?.id === polygonId) {
                            setSelectedPolygon(null);
                        }
                    },
                    onError: (err) => {
                        console.error('[useDocumentPolygons] Failed to delete polygon:', err);
                    },
                }
            );
        },
        [documentId, selectedPolygon, deleteMutation]
    );

    // Select a polygon
    const selectPolygon = useCallback(
        (polygonId: string | null) => {
            if (!polygonId) {
                setSelectedPolygon(null);
                return;
            }

            const found = polygons.find((p) => p.id === polygonId);
            setSelectedPolygon(found || null);
        },
        [polygons]
    );

    return {
        polygons,
        selectedPolygon,
        createPolygon,
        updatePolygon,
        deletePolygon,
        selectPolygon,
        setSelectedPolygon,
        // Expose loading/error states
        isLoading,
        error,
        // Expose mutation states for UI feedback
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
}
